/* IndexedDB layer for offline field-entry capture */
import { openDB } from "idb";

const DB_NAME = "btp_studio_offline";
const DB_VERSION = 1;
const STORE_ENTRIES = "field_entries";   // local entries (synced or not)
const STORE_META = "meta";                 // last sync, cached lookups

let _dbPromise;

function getDB() {
  if (!_dbPromise) {
    _dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_ENTRIES)) {
          const s = db.createObjectStore(STORE_ENTRIES, { keyPath: "local_id" });
          s.createIndex("by_synced", "synced");
          s.createIndex("by_mission", "mission_id");
          s.createIndex("by_captured", "captured_at");
        }
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META, { keyPath: "key" });
        }
      },
    });
  }
  return _dbPromise;
}

export async function saveEntry(entry) {
  const db = await getDB();
  const local_id = entry.local_id || `loc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const record = {
    local_id,
    server_id: entry.server_id || null,
    synced: false,
    captured_at: entry.captured_at || new Date().toISOString(),
    mission_id: entry.mission_id || "",
    project_id: entry.project_id || "",
    type: entry.type || "observation", // observation | photo | mesure
    title: entry.title || "",
    description: entry.description || "",
    photo_b64: entry.photo_b64 || null,
    gps: entry.gps || null, // { lat, lon, accuracy }
    measurements: entry.measurements || null,
  };
  await db.put(STORE_ENTRIES, record);
  return record;
}

export async function listEntries() {
  const db = await getDB();
  const all = await db.getAll(STORE_ENTRIES);
  return all.sort((a, b) => (b.captured_at || "").localeCompare(a.captured_at || ""));
}

export async function pendingCount() {
  const db = await getDB();
  const idx = db.transaction(STORE_ENTRIES).store.index("by_synced");
  // IDB cannot index booleans natively; iterate and count
  const all = await db.getAll(STORE_ENTRIES);
  return all.filter((e) => !e.synced).length;
}

export async function getPending() {
  const all = await listEntries();
  return all.filter((e) => !e.synced);
}

export async function markSynced(local_id, server_id) {
  const db = await getDB();
  const rec = await db.get(STORE_ENTRIES, local_id);
  if (rec) {
    rec.synced = true;
    rec.server_id = server_id;
    await db.put(STORE_ENTRIES, rec);
  }
}

export async function deleteEntry(local_id) {
  const db = await getDB();
  await db.delete(STORE_ENTRIES, local_id);
}

export async function setMeta(key, value) {
  const db = await getDB();
  await db.put(STORE_META, { key, value, updated_at: new Date().toISOString() });
}

export async function getMeta(key) {
  const db = await getDB();
  const r = await db.get(STORE_META, key);
  return r?.value;
}
